import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Lock, Mail, Calendar, LogOut, ArrowLeft, Send, Image, Trash2, Plus, Upload, Link } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [subscribers, setSubscribers] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState("subscribers");
  const [galleryImages, setGalleryImages] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [newImageAlt, setNewImageAlt] = useState("");
  const [uploadMode, setUploadMode] = useState("file"); // "file" or "url"
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setToken(response.data.token);
        fetchSubscribers(response.data.token);
        fetchGallery();
        
        // Also login to competition system as admin
        try {
          const compResponse = await axios.post(`${API}/competition/admin-login?password=${encodeURIComponent(password)}`);
          if (compResponse.data.success) {
            localStorage.setItem("comp_token", compResponse.data.token);
            localStorage.setItem("comp_admin", "true");
          }
        } catch (compErr) {
          // Silently fail - competition admin login is optional
          console.log("Competition admin login skipped");
        }
      } else {
        setError("Hibás jelszó!");
      }
    } catch (err) {
      setError("Hiba történt a bejelentkezés során.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async (adminToken) => {
    try {
      const response = await axios.get(`${API}/newsletter/subscriptions?token=${adminToken}`);
      setSubscribers(response.data);
    } catch (err) {
      setError("Nem sikerült betölteni a feliratkozókat.");
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGalleryImages(response.data);
    } catch (err) {
      console.error("Failed to fetch gallery");
    }
  };

  const handleLogout = () => {
    setToken("");
    setSubscribers([]);
    setPassword("");
    setGalleryImages([]);
    // Also logout from competition system
    localStorage.removeItem("comp_token");
    localStorage.removeItem("comp_admin");
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error("Kérlek töltsd ki a tárgyat és az üzenetet!");
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/broadcast`, {
        token,
        subject,
        message
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setSubject("");
        setMessage("");
        setShowBroadcast(false);
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      toast.error("Hiba történt az email küldése során.");
    } finally {
      setSending(false);
    }
  };

  const handleAddImage = async (e) => {
    e.preventDefault();
    
    if (uploadMode === "url") {
      if (!newImageUrl.trim()) {
        toast.error("Kérlek add meg a kép URL-jét!");
        return;
      }

      try {
        const response = await axios.post(`${API}/admin/gallery/add?token=${token}`, {
          url: newImageUrl,
          alt: newImageAlt || "Galéria kép"
        });
        
        setGalleryImages([...galleryImages, response.data]);
        setNewImageUrl("");
        setNewImageAlt("");
        toast.success("Kép hozzáadva!");
      } catch (err) {
        toast.error("Hiba történt a kép hozzáadása során.");
      }
    } else {
      if (!selectedFile) {
        toast.error("Kérlek válassz ki egy képet!");
        return;
      }

      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("token", token);
        formData.append("alt", newImageAlt || selectedFile.name);

        const response = await axios.post(`${API}/admin/gallery/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        const imageData = response.data;
        if (imageData.url.startsWith("/api/uploads/")) {
          imageData.url = `${BACKEND_URL}${imageData.url}`;
        }
        
        setGalleryImages([...galleryImages, imageData]);
        setSelectedFile(null);
        setNewImageAlt("");
        // Reset file input
        const fileInput = document.getElementById("gallery-file-input");
        if (fileInput) fileInput.value = "";
        toast.success("Kép feltöltve!");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Hiba történt a kép feltöltése során.");
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await axios.delete(`${API}/admin/gallery/delete?token=${token}&image_id=${imageId}`);
      setGalleryImages(galleryImages.filter(img => img.id !== imageId));
      toast.success("Kép törölve!");
    } catch (err) {
      toast.error("Hiba történt a kép törlése során.");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <div className="w-full max-w-md">
          <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                <Lock className="w-8 h-8 text-[#D4AF37]" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white text-center mb-2">Admin Belépés</h1>
            <p className="text-white/60 text-center mb-8">Hírlevél és galéria kezelése</p>
            
            <form onSubmit={handleLogin}>
              <Input
                type="password"
                placeholder="Jelszó"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full mb-4 bg-[#0A0A0A] border-[rgba(212,175,55,0.3)] text-white placeholder:text-white/40"
                data-testid="admin-password-input"
              />
              {error && (
                <p className="text-red-500 text-sm mb-4 text-center">{error}</p>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-medium"
                data-testid="admin-login-btn"
              >
                {loading ? "Bejelentkezés..." : "Belépés"}
              </Button>
            </form>
            
            <a
              href="/"
              className="flex items-center justify-center gap-2 mt-6 text-white/40 hover:text-[#D4AF37] transition-colors text-sm"
            >
              <ArrowLeft size={16} />
              Vissza a főoldalra
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-4 md:p-8">
      <Toaster position="top-center" richColors />
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} className="mr-2" />
            Kilépés
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("subscribers")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "subscribers"
                ? "bg-[#D4AF37] text-[#0A0A0A]"
                : "bg-[#141414] text-white/60 hover:text-white"
            }`}
          >
            <Mail size={18} className="inline mr-2" />
            Feliratkozók ({subscribers.length})
          </button>
          <button
            onClick={() => setActiveTab("gallery")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "gallery"
                ? "bg-[#D4AF37] text-[#0A0A0A]"
                : "bg-[#141414] text-white/60 hover:text-white"
            }`}
          >
            <Image size={18} className="inline mr-2" />
            Galéria ({galleryImages.length})
          </button>
        </div>

        {/* Subscribers Tab */}
        {activeTab === "subscribers" && (
          <>
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => setShowBroadcast(!showBroadcast)}
                className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
                data-testid="broadcast-toggle-btn"
              >
                <Send size={18} className="mr-2" />
                Email küldése mindenkinek
              </Button>
            </div>

            {showBroadcast && (
              <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg p-6 mb-6">
                <h2 className="text-xl font-bold text-white mb-4">Email küldése az összes feliratkozónak</h2>
                <form onSubmit={handleBroadcast}>
                  <div className="mb-4">
                    <label className="text-white/60 text-sm mb-2 block">Tárgy</label>
                    <Input
                      type="text"
                      placeholder="Email tárgya..."
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="w-full bg-[#0A0A0A] border-[rgba(212,175,55,0.3)] text-white placeholder:text-white/40"
                      data-testid="broadcast-subject-input"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="text-white/60 text-sm mb-2 block">Üzenet</label>
                    <textarea
                      placeholder="Email szövege..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={6}
                      className="w-full bg-[#0A0A0A] border border-[rgba(212,175,55,0.3)] rounded-md text-white placeholder:text-white/40 p-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      data-testid="broadcast-message-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={sending}
                      className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
                      data-testid="broadcast-send-btn"
                    >
                      {sending ? "Küldés..." : `Küldés ${subscribers.length} címre`}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setShowBroadcast(false)}
                      variant="outline"
                      className="border-white/20 text-white/60 hover:bg-white/10"
                    >
                      Mégse
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg overflow-hidden">
              {subscribers.length === 0 ? (
                <div className="p-12 text-center">
                  <Mail className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">Még nincs feliratkozó.</p>
                </div>
              ) : (
                <div className="divide-y divide-[rgba(255,255,255,0.1)]">
                  {subscribers.map((sub, index) => (
                    <div
                      key={sub.id}
                      className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-white/5 transition-colors"
                      data-testid={`subscriber-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-[#D4AF37]" />
                        </div>
                        <span className="text-white font-medium">{sub.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-white/40 text-sm">
                        <Calendar size={14} />
                        {formatDate(sub.subscribed_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Gallery Tab */}
        {activeTab === "gallery" && (
          <>
            <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg p-6 mb-6">
              <h2 className="text-lg font-bold text-white mb-4">Új kép hozzáadása</h2>
              
              {/* Upload mode toggle */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setUploadMode("file")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    uploadMode === "file"
                      ? "bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]"
                      : "bg-[#0A0A0A] text-white/60 border border-white/10 hover:text-white"
                  }`}
                >
                  <Upload size={16} />
                  Fájl feltöltés
                </button>
                <button
                  onClick={() => setUploadMode("url")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    uploadMode === "url"
                      ? "bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]"
                      : "bg-[#0A0A0A] text-white/60 border border-white/10 hover:text-white"
                  }`}
                >
                  <Link size={16} />
                  URL megadás
                </button>
              </div>

              <form onSubmit={handleAddImage} className="space-y-4">
                {uploadMode === "file" ? (
                  <div>
                    <label className="text-white/60 text-sm mb-2 block">Kép kiválasztása</label>
                    <input
                      id="gallery-file-input"
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      className="w-full bg-[#0A0A0A] border border-[rgba(212,175,55,0.3)] rounded-md text-white p-3 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#D4AF37] file:text-[#0A0A0A] hover:file:bg-[#F3E5AB] cursor-pointer"
                      data-testid="gallery-file-input"
                    />
                    {selectedFile && (
                      <p className="text-white/40 text-sm mt-2">Kiválasztva: {selectedFile.name}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="text-white/60 text-sm mb-2 block">Kép URL</label>
                    <Input
                      type="url"
                      placeholder="https://..."
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      className="w-full bg-[#0A0A0A] border-[rgba(212,175,55,0.3)] text-white placeholder:text-white/40"
                      data-testid="gallery-url-input"
                    />
                  </div>
                )}
                
                <div>
                  <label className="text-white/60 text-sm mb-2 block">Leírás (opcionális)</label>
                  <Input
                    type="text"
                    placeholder="Kép leírása..."
                    value={newImageAlt}
                    onChange={(e) => setNewImageAlt(e.target.value)}
                    className="w-full bg-[#0A0A0A] border-[rgba(212,175,55,0.3)] text-white placeholder:text-white/40"
                    data-testid="gallery-alt-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={uploading}
                  className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
                  data-testid="gallery-add-btn"
                >
                  {uploading ? (
                    "Feltöltés..."
                  ) : (
                    <>
                      <Plus size={18} className="mr-2" />
                      {uploadMode === "file" ? "Feltöltés" : "Hozzáadás"}
                    </>
                  )}
                </Button>
              </form>
            </div>

            <div className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg overflow-hidden">
              {galleryImages.length === 0 ? (
                <div className="p-12 text-center">
                  <Image className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">Még nincs kép a galériában.</p>
                  <p className="text-white/40 text-sm mt-2">Adj hozzá képeket a fenti űrlap segítségével.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4">
                  {galleryImages.map((img) => (
                    <div key={img.id} className="relative group">
                      <img
                        src={img.url}
                        alt={img.alt}
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => handleDeleteImage(img.id)}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`gallery-delete-${img.id}`}
                      >
                        <Trash2 size={16} className="text-white" />
                      </button>
                      {img.alt && (
                        <p className="text-white/60 text-xs mt-1 truncate">{img.alt}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
        
        <a
          href="/"
          className="flex items-center justify-center gap-2 mt-8 text-white/40 hover:text-[#D4AF37] transition-colors"
        >
          <ArrowLeft size={16} />
          Vissza a főoldalra
        </a>
      </div>
    </div>
  );
}
